
import React, { useState, useMemo } from 'react';
import { useAppContext } from '../contexts/AppContext';
import { Customer, CustomerRequirement } from '../types';
import { FileText, Plus, Filter, Search, CheckSquare, List } from 'lucide-react';
import ActiveProjectBadge from '../components/ActiveProjectBadge';

// --- Types for internal state ---
type ViewMode = 'list' | 'review';

const CustomerRequirementsPage: React.FC = () => {
    const { allData, activeProject, approveRequirement } = useAppContext();
    const [selectedCustomerId, setSelectedCustomerId] = useState<string>('all');
    // selectedProductId removed
    const [viewMode, setViewMode] = useState<ViewMode>('list');
    const [searchTerm, setSearchTerm] = useState('');

    // --- Derived Data ---
    const filteredCustomers = useMemo(() => {
        if (activeProject === 'all') return allData.customers;
        return allData.customers.filter(c => c.projectName === activeProject);
    }, [allData.customers, activeProject]);

    const filteredRequirements = useMemo(() => {
        let base = allData.customerRequirements;

        // Filter by project first
        if (activeProject !== 'all') {
            base = base.filter(req => req.projectName === activeProject);
        }

        return base.filter(req => {
            const matchesCustomer = selectedCustomerId === 'all' || req.customerId === selectedCustomerId;

            const matchesSearch = req.requirementText.toLowerCase().includes(searchTerm.toLowerCase()) ||
                req.type.toLowerCase().includes(searchTerm.toLowerCase());

            return matchesCustomer && matchesSearch;
        });
    }, [allData.customerRequirements, activeProject, selectedCustomerId, searchTerm]);

    const stats = useMemo(() => {
        const total = filteredRequirements.length;
        const critical = filteredRequirements.filter(r => r.priority === 'Critical').length;
        const safety = filteredRequirements.filter(r => r.type === 'Safety').length;
        return { total, critical, safety };
    }, [filteredRequirements]);

    // --- Add Customer / Requirement Logic ---
    const { addCustomer, addCustomerRequirement } = useAppContext();
    const [showCustomerModal, setShowCustomerModal] = useState(false);
    const [showRequirementModal, setShowRequirementModal] = useState(false);

    // Form States
    const [newCustomer, setNewCustomer] = useState<Partial<Customer>>({ name: '', industry: '', tier: 'OEM', countryId: 'USA' });
    const [newRequirement, setNewRequirement] = useState<Partial<CustomerRequirement>>({
        requirementText: '',
        type: 'Performance',
        priority: 'High',
        metric: '',
        target: '',
        customerId: ''
    });

    const handleSaveCustomer = async () => {
        if (!newCustomer.name) return alert("Customer Name is required");
        const id = `CU-${Date.now().toString().slice(-4)}`;
        await addCustomer({
            ...newCustomer,
            id,
            projectName: activeProject !== 'all' ? activeProject : undefined,
            epochId: activeProject !== 'all' ? 1 : undefined
        } as Customer);
        setShowCustomerModal(false);
        setNewCustomer({ name: '', industry: '', tier: 'OEM', countryId: 'USA' });
        alert("Customer Added!");
    };

    const handleSaveRequirement = async () => {
        if (!newRequirement.requirementText || !newRequirement.customerId) return alert("Requirement and Customer are required");
        const id = `CR-${Date.now().toString().slice(-4)}`;
        await addCustomerRequirement({
            ...newRequirement,
            id,
            linkedProductFeatureIds: [],
            projectName: activeProject !== 'all' ? activeProject : undefined,
            epochId: activeProject !== 'all' ? 1 : undefined
        } as CustomerRequirement);
        setShowRequirementModal(false);
        setNewRequirement({ requirementText: '', type: 'Performance', priority: 'High', metric: '', target: '', customerId: '' });
        alert("Requirement Added!");
    };

    return (
        <div className="space-y-6 h-full flex flex-col relative">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-card p-6 rounded-lg border border-border shadow-sm">
                <div>
                    <h1 className="text-3xl font-bold text-text-primary flex items-center gap-3">
                        <FileText className="text-primary" size={32} />
                        Customer Requirements
                    </h1>
                    <p className="text-text-secondary mt-1">
                        Manage Technical Requirements Specifications (TRS) and trace them to product features.
                    </p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={() => setShowCustomerModal(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 border border-border rounded-lg text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                    >
                        <Plus size={16} /> Add Customer
                    </button>
                    <button
                        onClick={() => setShowRequirementModal(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors shadow-sm"
                    >
                        <Plus size={16} /> Add Requirement
                    </button>
                </div>
            </div>

            {/* Filter & Stats Bar */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                {/* Filters */}
                <div className="lg:col-span-3 bg-card border border-border rounded-lg p-4 flex flex-wrap gap-4 items-center">
                    <div className="flex items-center gap-2 text-sm font-medium text-text-secondary">
                        <Filter size={16} /> Filters:
                    </div>

                    <ActiveProjectBadge showStandard={false} />

                    <select
                        value={selectedCustomerId}
                        onChange={(e) => setSelectedCustomerId(e.target.value)}
                        className="bg-background border border-border rounded-md px-3 py-1.5 text-sm min-w-[200px]"
                    >
                        <option value="all">All Customers</option>
                        {filteredCustomers.map(c => (
                            <option key={c.id} value={c.id}>{c.name} ({c.tier})</option>
                        ))}
                    </select>

                    <div className="relative flex-grow max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" size={16} />
                        <input
                            type="text"
                            placeholder="Search requirements..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-background border border-border rounded-md pl-9 pr-3 py-1.5 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                        />
                    </div>
                </div>

                {/* Mini Stats */}
                <div className="bg-card border border-border rounded-lg p-4 flex justify-between items-center text-sm">
                    <div className="text-center">
                        <div className="text-2xl font-bold text-text-primary">{stats.total}</div>
                        <div className="text-text-secondary text-xs uppercase">Total</div>
                    </div>
                    <div className="w-px h-8 bg-border"></div>
                    <div className="text-center text-red-600">
                        <div className="text-2xl font-bold">{stats.critical}</div>
                        <div className="text-xs uppercase opacity-80">Critical</div>
                    </div>
                    <div className="w-px h-8 bg-border"></div>
                    <div className="text-center text-orange-600">
                        <div className="text-2xl font-bold">{stats.safety}</div>
                        <div className="text-xs uppercase opacity-80">Safety</div>
                    </div>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="bg-card border border-border rounded-lg shadow-sm flex-1 overflow-hidden flex flex-col">
                <div className="p-4 border-b border-border bg-slate-50 dark:bg-slate-900/50 flex justify-between items-center">
                    <h2 className="font-bold text-lg text-text-primary flex items-center gap-2">
                        {viewMode === 'list' ? <List size={20} /> : <CheckSquare size={20} />}
                        Requirements List
                    </h2>
                    <div className="flex bg-background border border-border rounded-lg p-1">
                        <button
                            onClick={() => setViewMode('list')}
                            className={`px-3 py-1.5 text-xs font-medium rounded ${viewMode === 'list' ? 'bg-primary text-white' : 'text-text-secondary hover:text-text-primary'}`}
                        >
                            List View
                        </button>
                        <button
                            onClick={() => setViewMode('review')}
                            className={`px-3 py-1.5 text-xs font-medium rounded ${viewMode === 'review' ? 'bg-primary text-white' : 'text-text-secondary hover:text-text-primary'}`}
                        >
                            TRS Review
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-slate-50 dark:bg-slate-900/50 sticky top-0 z-10 text-xs font-bold text-text-secondary uppercase tracking-wider">
                            <tr>
                                <th className="py-3 px-4 border-b border-border w-24">ID</th>
                                <th className="py-3 px-4 border-b border-border">Requirement</th>
                                <th className="py-3 px-4 border-b border-border w-32">Type</th>
                                <th className="py-3 px-4 border-b border-border w-24">Priority</th>
                                <th className="py-3 px-4 border-b border-border w-48">Metric / Target</th>
                                {viewMode === 'review' && <th className="py-3 px-4 border-b border-border w-32 text-center">Status</th>}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {filteredRequirements.map(req => (
                                <tr key={req.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                    <td className="py-3 px-4 font-mono text-xs text-text-secondary">{req.id}</td>
                                    <td className="py-3 px-4 text-sm text-text-primary">
                                        {req.requirementText}
                                        <div className="text-xs text-text-secondary mt-1 flex gap-2">
                                            {req.linkedProductFeatureIds && req.linkedProductFeatureIds.length > 0 && (
                                                <span className="bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded flex items-center gap-1">
                                                    Linked to {req.linkedProductFeatureIds.length} Feature(s)
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="py-3 px-4 text-sm">
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getTypeColor(req.type)}`}>
                                            {req.type}
                                        </span>
                                    </td>
                                    <td className="py-3 px-4 text-sm">
                                        <span className={`font-bold ${getPriorityColor(req.priority)}`}>
                                            {req.priority}
                                        </span>
                                    </td>
                                    <td className="py-3 px-4 text-xs text-text-secondary">
                                        {req.metric && <div className="font-semibold text-text-primary">{req.metric}</div>}
                                        {req.target && <div className="italic">{req.target}</div>}
                                    </td>
                                    {viewMode === 'review' && (
                                        <td className="py-3 px-4 text-center">
                                            <div className="flex justify-center gap-2">
                                                <button
                                                    onClick={() => approveRequirement(req.id)}
                                                    className="p-1 text-green-600 hover:bg-green-100 rounded"
                                                    title="Approve"
                                                >
                                                    <CheckSquare size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))}
                            {filteredRequirements.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="py-12 text-center text-text-secondary">
                                        No requirements found matching your criteria.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modals */}
            {showCustomerModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-card w-full max-w-md rounded-lg shadow-xl border border-border p-6 animate-in slide-in-from-bottom-4 fade-in">
                        <h2 className="text-xl font-bold mb-4">Add New Customer</h2>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Customer Name</label>
                                <input
                                    className="w-full bg-background border border-border rounded p-2"
                                    value={newCustomer.name}
                                    onChange={e => setNewCustomer({ ...newCustomer, name: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Industry</label>
                                <input
                                    className="w-full bg-background border border-border rounded p-2"
                                    value={newCustomer.industry}
                                    onChange={e => setNewCustomer({ ...newCustomer, industry: e.target.value })}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Tier</label>
                                    <select
                                        className="w-full bg-background border border-border rounded p-2"
                                        value={newCustomer.tier}
                                        onChange={e => setNewCustomer({ ...newCustomer, tier: e.target.value })}
                                    >
                                        <option>OEM</option>
                                        <option>Tier 1</option>
                                        <option>Tier 2</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Country ID</label>
                                    <input
                                        className="w-full bg-background border border-border rounded p-2"
                                        value={newCustomer.countryId}
                                        onChange={e => setNewCustomer({ ...newCustomer, countryId: e.target.value })}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end gap-2">
                            <button onClick={() => setShowCustomerModal(false)} className="px-4 py-2 text-text-secondary hover:bg-slate-100 dark:hover:bg-slate-800 rounded">Cancel</button>
                            <button onClick={handleSaveCustomer} className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90">Save Customer</button>
                        </div>
                    </div>
                </div>
            )}

            {showRequirementModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-card w-full max-w-lg rounded-lg shadow-xl border border-border p-6 animate-in slide-in-from-bottom-4 fade-in">
                        <h2 className="text-xl font-bold mb-4">Add Requirement</h2>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Customer</label>
                                <select
                                    className="w-full bg-background border border-border rounded p-2"
                                    value={newRequirement.customerId}
                                    onChange={e => setNewRequirement({ ...newRequirement, customerId: e.target.value })}
                                >
                                    <option value="">-- Select Customer --</option>
                                    {filteredCustomers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Requirement Text</label>
                                <textarea
                                    className="w-full bg-background border border-border rounded p-2 h-24"
                                    value={newRequirement.requirementText}
                                    onChange={e => setNewRequirement({ ...newRequirement, requirementText: e.target.value })}
                                    placeholder="e.g., The system must operate at -40C..."
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Type</label>
                                    <select
                                        className="w-full bg-background border border-border rounded p-2"
                                        value={newRequirement.type}
                                        onChange={e => setNewRequirement({ ...newRequirement, type: e.target.value as any })}
                                    >
                                        <option>Performance</option>
                                        <option>Safety</option>
                                        <option>Regulatory</option>
                                        <option>Durability</option>
                                        <option>Usability</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Priority</label>
                                    <select
                                        className="w-full bg-background border border-border rounded p-2"
                                        value={newRequirement.priority}
                                        onChange={e => setNewRequirement({ ...newRequirement, priority: e.target.value as any })}
                                    >
                                        <option>Critical</option>
                                        <option>High</option>
                                        <option>Medium</option>
                                        <option>Low</option>
                                    </select>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Metric</label>
                                    <input
                                        className="w-full bg-background border border-border rounded p-2"
                                        value={newRequirement.metric}
                                        onChange={e => setNewRequirement({ ...newRequirement, metric: e.target.value })}
                                        placeholder="e.g. Temperature"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Target</label>
                                    <input
                                        className="w-full bg-background border border-border rounded p-2"
                                        value={newRequirement.target}
                                        onChange={e => setNewRequirement({ ...newRequirement, target: e.target.value })}
                                        placeholder="e.g. >= -40C"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end gap-2">
                            <button onClick={() => setShowRequirementModal(false)} className="px-4 py-2 text-text-secondary hover:bg-slate-100 dark:hover:bg-slate-800 rounded">Cancel</button>
                            <button onClick={handleSaveRequirement} className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90">Save Requirement</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- Helpers ---
function getTypeColor(type: string): string {
    switch (type) {
        case 'Performance': return 'bg-blue-50 text-blue-700 border-blue-200';
        case 'Safety': return 'bg-red-50 text-red-700 border-red-200';
        case 'Regulatory': return 'bg-purple-50 text-purple-700 border-purple-200';
        case 'Durability': return 'bg-orange-50 text-orange-700 border-orange-200';
        case 'Usability': return 'bg-green-50 text-green-700 border-green-200';
        default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
}

function getPriorityColor(priority: string): string {
    switch (priority) {
        case 'Critical': return 'text-red-600';
        case 'High': return 'text-orange-600';
        case 'Medium': return 'text-blue-600';
        default: return 'text-gray-500';
    }
}

export default CustomerRequirementsPage;
